/**
 * Resolves the dispatched date for each goods receipt.
 *
 * A non-Barscan goods receipt is traced back to the order it was raised for through
 * order request -> purchase order -> sourceOrder, the same walk the allocate drawer uses;
 * its dispatched date is the source order's sage_despatched_at, set when it was despatched in Sage.
 * Barscan receipts instead carry their own despatch date straight from Barscan.
 * Branch-to-branch transfers have neither, so they fall back to the date stock was transferred
 * out of the sending branch - the "Transferred" table on the goods receipt page.
 */
export const sourceOrderDespatchAttribute = {
  name: "orderRequest",
  attributes: [
    {
      name: "purchase_order",
      attributes: [{ name: "sourceOrder", attributes: ["sage_despatched_at"] }],
    },
  ],
}

/** Stock transfers out of the order's sending branch, for branch-to-branch transfer dates */
export const sentStockTransfersAttribute = {
  name: "stock_transfers_generated",
  attributes: [{ name: "branch", attributes: [] }, "transfer_date"],
}

export type ISourceOrderDespatchFields = {
  id: string
  branch?: { id: string } | null
  stock_transfers_generated?: {
    branch?: { id: string } | null
    transfer_date?: Date | string | null
  }[]
  childOrder?: { id: string } | null
  barscanInternalOrderTransferId?: number | null
  barscan_despatched_at?: Date | string | null
  orderRequest?: {
    id?: string
    purchase_order?: {
      id?: string
      sourceOrder?: { sage_despatched_at?: string | Date | null } | null
    } | null
  } | null
}

/** Matches the limit findChildOrderRequest uses when walking back order chains */
const maxChildOrderDepth = 20

function despatchDateOf(order: ISourceOrderDespatchFields) {
  const value = order.orderRequest?.purchase_order?.sourceOrder?.sage_despatched_at
  return value ? new Date(value) : null
}

/** Earliest transfer of stock out of the sending branch, i.e. when it left for the receiver */
function sentTransferDateOf(order: ISourceOrderDespatchFields) {
  const times = (order.stock_transfers_generated ?? [])
    .filter(transfer => transfer.transfer_date && transfer.branch?.id === order.branch?.id)
    .map(transfer => new Date(transfer.transfer_date!).getTime())
  return times.length > 0 ? new Date(Math.min(...times)) : null
}

/**
 * Maps each order id to the dispatched date of the order its goods were raised for. Back orders
 * often have no order request of their own, so like findChildOrderRequest this follows childOrder
 * until it reaches an order that has one. Each level of every chain is fetched in a single query,
 * so a page of back orders costs a few queries rather than one per row.
 */
export async function loadSourceOrderDespatchDates(orders: ISourceOrderDespatchFields[]) {
  const dates = new Map<string, Date | null>()
  // order id -> the order further down its chain that still needs looking at
  let pending = new Map<string, string>()
  const visited = new Map<string, Set<string>>()

  for (const order of orders) {
    const ownDate = despatchDateOf(order) ?? sentTransferDateOf(order)
    if (order.barscanInternalOrderTransferId) {
      // Barscan receipts carry their own despatch date from Barscan - no source-order walk.
      dates.set(
        order.id,
        order.barscan_despatched_at ? new Date(order.barscan_despatched_at) : null,
      )
    } else if (ownDate) {
      dates.set(order.id, ownDate)
    } else if (!order.orderRequest && order.childOrder) {
      pending.set(order.id, order.childOrder.id)
      visited.set(order.id, new Set([order.id]))
    } else {
      dates.set(order.id, null)
    }
  }

  try {
    for (let depth = 0; pending.size > 0 && depth < maxChildOrderDepth; depth++) {
      const children = await window.skeem.fetch2<ISourceOrderDespatchFields[]>({
        model: {
          name: "orders",
          attributes: [{ name: "childOrder", attributes: [] }, sourceOrderDespatchAttribute],
          filter: { matches: { id: { or: [...new Set(pending.values())] } } },
        },
      })
      const childrenById = new Map(children.map(child => [child.id, child]))

      const next = new Map<string, string>()
      for (const [orderId, childId] of pending) {
        const child = childrenById.get(childId)
        const seen = visited.get(orderId)!
        if (child && !child.orderRequest && child.childOrder && !seen.has(child.childOrder.id)) {
          seen.add(childId)
          next.set(orderId, child.childOrder.id)
        } else {
          dates.set(orderId, child ? despatchDateOf(child) : null)
        }
      }
      pending = next
    }
  } catch (e) {
    console.error("Error finding the source order for back orders", e)
  }

  // Chains that errored, looped or ran too deep show no date rather than breaking the table
  for (const orderId of pending.keys()) {
    dates.set(orderId, null)
  }
  return dates
}
